import { AvatarImage } from './avatar-image'
import { useVoiceCall } from '../contexts/voice-call-context'
import { useResolvedPresence } from '../stores/presence-store'
import { useWaitingCall } from '../stores/waiting-call-store'

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24 11.36 11.36 0 003.56.57 1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1 11.36 11.36 0 00.57 3.56 1 1 0 01-.25 1.01l-2.2 2.22z" />
    </svg>
  )
}

function MergeIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 3v4a4 4 0 004 4h4m0 0l-3-3m3 3l-3 3M4 21l6-6" />
    </svg>
  )
}

/**
 * Call-waiting banner: a second incoming 1:1 call arrived while the user is in an
 * active call. Offers MERGE (add the new caller into the current call, upgrading
 * 1:1 → group) or DECLINE. Rendered above the call overlays but below modal
 * dialogs. Works on web and inside the native WebView.
 */
export function CallWaitingPrompt() {
  const waiting = useWaitingCall()
  const { mergeWaitingCall, declineWaitingCall } = useVoiceCall()
  const presence = useResolvedPresence(waiting?.peer)

  if (!waiting) return null

  const mediaLabel = waiting.mediaType === 'video' ? 'video call' : 'call'

  return (
    <div
      className="fixed inset-x-0 top-[max(0.75rem,var(--safe-top))] z-[65] flex justify-center px-3"
      role="alertdialog"
      aria-modal="false"
      aria-label={`Incoming ${mediaLabel} from ${waiting.peer.displayName}`}
    >
      <div className="animate-fade-slide-in flex w-full max-w-md items-center gap-3 rounded-2xl border border-white/10 bg-slate-900/95 p-3 text-white shadow-2xl backdrop-blur">
        <AvatarImage
          name={waiting.peer.displayName}
          avatarUrl={waiting.peer.avatarUrl}
          size={44}
          variant="provider"
          presence={presence}
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{waiting.peer.displayName}</p>
          <p className="truncate text-xs text-white/70">Incoming {mediaLabel} · call waiting</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => declineWaitingCall()}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500 text-white shadow transition hover:bg-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            aria-label={`Decline ${mediaLabel} from ${waiting.peer.displayName}`}
            title="Decline"
          >
            <PhoneIcon className="h-5 w-5 rotate-[135deg]" />
          </button>
          <button
            type="button"
            onClick={() => void mergeWaitingCall()}
            className="flex items-center gap-1.5 rounded-full bg-emerald-500 px-3.5 py-2 text-sm font-semibold text-white shadow transition hover:bg-emerald-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            aria-label={`Merge ${waiting.peer.displayName} into the call`}
            title="Merge into call"
          >
            <MergeIcon />
            Merge
          </button>
        </div>
      </div>
    </div>
  )
}
